import {makeAutoObservable} from "mobx";
import {displayErrorDialogPrompt} from "../common/util/errror/log.util";
import {getDayPath, getDefaultContentUrl} from "./ContentStore";
import {VoiceType} from "../common/enums/VoiceType";
import {langTypes} from "../common/enums/ExcelEnum";

const logPrefix  = "[OddStepTwoStore]"
export default class OddStepTwoStore {

    constructor(props) {
        this.contentRepository = props.contentRepository;
        this.contentStore = props.contentStore;
        this.dialogStore = props.dialogStore;
        this.commonStore = props.commonStore;

        this.contentTapIndex = 0;
        this.audio = null;
        this.image = '';
        this.voiceType = VoiceType.MAN;
        this.contentList = [];
        this.selectedContent = '';
        this.selectedSubContent = 0;
        this.timeoutId = 0;
        this.isProcessingPosition = false;
        this.positionList = [];
        this.popoverState = false;

        makeAutoObservable(this);
    }

    init = () => {
        this.setSelectedContent();
        this.selectedSubContent = 0;
        this.isProcessingPosition = false;
        this.positionList = [];
        this.voiceType = this.getSelectedContent().voiceType;
    }

    setContentTapIndex = (index) => {
        this.contentTapIndex = index;
        this.init();
    };

    openErrorDialog = (info) => {
        if (this?.dialogStore) {
            this.dialogStore.openErrorDialog(info)
        }
    }

    setVoiceType = (voiceType) => {
        this.voiceType = voiceType;
    }

    setSelectedContent = () => {
        this.selectedContent = this.getSelectedContent(this.getSelectedLangCode());
    }

    setSelectedSubContent = (seq) => {
        this.selectedSubContent = seq;
    }

    setIsProcessingPosition = (isProcessing) => {
        this.isProcessingPosition = isProcessing;
    }

    getSelectedLangCode = () => {
        return langTypes[this.contentTapIndex];
    }

    getSelectedContent = () => {
        return this.contentList.find(content => content.languageCode === this.getSelectedLangCode());
    }

    getContent = (langeCode) => {
        return this.contentList.find(content => content.languageCode === langeCode);
    }

    setPositionList = (pointList) => {
        if (pointList) {
            this.positionList = pointList;
        } else {
            this.positionList = this.getPieceList(this.selectedSubContent).sort((a,b) => a.sequence - b.sequence).map((piece => {
                if (piece.x !== null && piece.y !== null) {
                    return {
                        x: piece.x,
                        y: piece.y
                    }
                }
                return null;
            })).filter((position) => position !== null);
        }
    }

    addPosition = (x, y) => {
        this.positionList.push({x: x, y: y});
    }

    setPosition = (index, x, y) => {
        if (x > 0 && y > 0) {
            this.positionList[index].x = x;
            this.positionList[index].y = y;
        }
    }

    savePosition = () => {
        for (let i = 0; i < this.positionList.length; i++) {
            for (let j = 0; j < this.contentList.length; j++) {
                const piece = this.contentList[j].pieces.find((piece) => piece.splitCount === this.getPieceCount() && piece.sequence === (i + 1));
                if (piece) {
                    piece.x = this.roundPosition(this.positionList[i].x);
                    piece.y = this.roundPosition(this.positionList[i].y);
                }
            }
        }
    }

    roundPosition = (position) => {
        let rounded = Math.round(position);

        if (rounded > 100) {
            return 100;
        }else if (rounded < 0) {
            return 0;
        } else {
            return rounded;
        }
    }

    getPieceList = (seq) => {
        const uniqueSplitCounts = Array.from(new Set(this.selectedContent.pieces.map(piece => piece.splitCount)));
        let splitCount = 0;
        if (uniqueSplitCounts.length === 0) {
            return [];
        } else if (uniqueSplitCounts.length === 1) {
            if (seq === 1) {
                splitCount = Math.min(...uniqueSplitCounts);
            } else {
                return [];
            }
        } else if (uniqueSplitCounts.length === 2) {
            if (seq === 1) {
                splitCount = Math.min(...uniqueSplitCounts);
            } else {
                splitCount = Math.max(...uniqueSplitCounts);
            }
        }

        return this.selectedContent.pieces.filter(piece => piece.splitCount === splitCount);
    }

    getPieceCount = () => {
        const uniqueSplitCounts = Array.from(new Set(this.selectedContent.pieces.map(piece => piece.splitCount)));

        if (uniqueSplitCounts.length === 0) {
            return 0;
        } else if (uniqueSplitCounts.length === 1) {
            if (this.selectedSubContent === 1) {
                return Math.min(...uniqueSplitCounts);
            } else {
                return 0;
            }
        } else if (uniqueSplitCounts.length === 2) {
            if (this.selectedSubContent === 1) {
                return Math.min(...uniqueSplitCounts);
            } else {
                return Math.max(...uniqueSplitCounts);
            }
        }
    }

    getDividedText = (start, end) => {
        return this.selectedContent.text.slice(start, end);
    }

    playAudio = (id, startTime, endTime) => {
        if (this.timeoutId !== 0) {
            clearTimeout(this.timeoutId);
        }
        if (this.audio && this.audio.id === id && !this.audio.paused) {
            this.stopAudio();
        } else {
            if (this.audio) {
                this.stopAudio();
            }
            const decodeAudio = atob(this.selectedContent.audio);
            const blob = new Blob([new Uint8Array(decodeAudio.length).map((_, i) => decodeAudio.charCodeAt(i))], { type: 'audio/mp3' });

            this.audio = new Audio();
            this.audio.id = id;
            this.audio.src = URL.createObjectURL(blob);

            if (startTime !== undefined && endTime !== undefined) {
                const duration = (endTime - startTime) * 1000;
                this.audio.currentTime = startTime;

                this.audio.play();
                this.timeoutId = setTimeout(() => {
                    if (this.audio) {
                        this.stopAudio();
                    }
                }, duration);
            } else {
                this.audio.play();
            }
        }
    }

    playPieceAudio = (pieceAudio) => {
        if (this.audio) {
            this.stopAudio();
        }

        const decodeAudio = atob(pieceAudio);
        const blob = new Blob([new Uint8Array(decodeAudio.length).map((_, i) => decodeAudio.charCodeAt(i))], { type: 'audio/mp3' });

        this.audio = new Audio();
        this.audio.src = URL.createObjectURL(blob);

        this.audio.play();
    }

    stopAudio = () => {
        if (this.audio) {
            this.audio.pause();
            this.audio = null;
        }
    }

    setSubContentAudios = (audioRegionList) => {
        if (audioRegionList.length > 0) {
            const langeCode = langTypes[this.contentTapIndex];
            const splitCount = this.getPieceCount();

            const content = this.contentList.filter(content => content.languageCode === langeCode)[0];
            const contentPieceList = content.pieces.filter(piece => piece.splitCount === splitCount);
            for (const contentPiece of contentPieceList) {
                contentPiece.audioStartTime = null;
                contentPiece.audioEndTime = null;
            }

            for (let i = 0; i < audioRegionList.length; i++) {
                let startTime = audioRegionList[i].start;
                let endTime = audioRegionList[i].end;

                const piece = contentPieceList.find(piece => piece.splitCount === splitCount && piece.sequence === i + 1);
                piece.audioStartTime = startTime;
                piece.audioEndTime = endTime;
            }
        }
    }

    changeMainAudio = (audio) => {
        this.selectedContent.audio = audio.split(',')[1];
        this.selectedContent.voiceType = this.voiceType;
        this.selectedContent.changeAudio = true;

        for (const piece of this.selectedContent.pieces) {
            piece.audioStartTime = null;
            piece.audioEndTime = null;
        }
    }

    setPopoverState = (boolean) => {
        if (this.popoverState !== null) {
            this.popoverState = boolean;
        }
    }

    * getOddStepTwoContent(step, day, callback) {
        this.commonStore.setLoadingState();
        try {
            console.log(logPrefix, 'Start getOddStepTwoContent.');
            const url = `${getDayPath(day)}/${getDefaultContentUrl(step, day)}/2`;
            const response = yield this.contentRepository.getContent(url);
            this.image = response.data.image;
            this.contentList = response.data.contents;
            this.init();
            console.log(logPrefix, 'Finished getOddStepTwoContent.');
            callback && callback(response.data);
        } catch (e) {
            console.log(logPrefix, 'Failed getOddStepTwoContent.');
            this.openErrorDialog({
                titleText: '컨텐츠 불러오기 오류',
                text: displayErrorDialogPrompt(e, '컨텐츠를 불러오는 도중 에러가 발생했습니다.')
            });
        } finally {
            this.commonStore.initLoadingState();
        }
    }

    * modifyOddStepTwoContent(step, day, callback) {
        this.commonStore.setLoadingState('데이터를 저장하는 중입니다.');
        try {
            console.log(logPrefix, 'Start modifyOddStepTwoContent.');
            const url = `${getDayPath(day)}/${getDefaultContentUrl(step, day)}/2`;
            const response = yield this.contentRepository.putContent(url, this.contentList);
            console.log(logPrefix, 'Finished modifyOddStepTwoContent : ', response);
            callback && callback();
        } catch (e) {
            this.commonStore.initLoadingState();
            console.log(logPrefix, 'Failed modifyOddStepTwoContent.');
            this.openErrorDialog({
                titleText: '컨텐츠 수정 오류',
                text: displayErrorDialogPrompt(e, '컨텐츠 수정 작업중 에러가 발생했습니다.')
            });
        }
    }

    * getPieceAudio(piece, callback) {
        try {
            console.log(logPrefix, 'Start getPieceAudio.');
            const filePath = piece.audioFilePath + '/' + piece.audioFileName;
            const response = yield this.contentRepository.getAudio(filePath);
            piece.audio = response.data;

            console.log(logPrefix, 'Finished getPieceAudio : ', response);
            callback && callback(piece.audio);
        } catch (e) {
            console.log(logPrefix, 'Failed getPieceAudio.');
        }
    }

    isOddStepTwoCompleted = (data) => {
        if (!data.image) {
            return false;
        }
        if (data.contents.length < 4) {
            return false;
        }

        for (let i = 0; i < 4; i++) {
            const content = data.contents[i];
            if (!content.audio) {
                return false;
            }
            const pieceList = content.pieces;
            for (const piece of pieceList) {
                if (piece.x === null || piece.y === null) {
                    return false;
                }
                if ((piece.audioStartTime == null || piece.audioEndTime === null) && (piece.audioFileName == null || piece.audioFilePath === null)) {
                    return false;
                }
            }

        }
        return true;
    }
}

